// import { m } from 'framer-motion';
// @mui
import Container from '@mui/material/Container';

// components
import Typography from '@mui/material/Typography';
import { Alert, Card, IconButton, Input, LinearProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Paper from '@mui/material/Paper';

import { useCallback, useEffect, useState } from 'react';
import axios, { endpoints } from 'src/utils/axios';
import { useTable, TablePaginationCustom } from 'src/components/table';
import Iconify from 'src/components/iconify';
import EmptyContent from 'src/components/empty-content';
import { format } from "date-fns";
import TableToolbar from './table-toolbar';
import DetailsDialog from './details-dialog';
// ----------------------------------------------------------------------

interface defaultRows {
  _id: number;
  year: number;
  student_code: string;
  student_id_card: string;
  student_dob: null;
  student_email: string;
  student_name: string;
  registration_number: string;
  type: number;
  issuance_place: any;
  issuance_date: any;
  number: string;
  rank: number;
  status: number;
  department: {
    _id: number;
    name: string;
    name_vn: string;
    code: string;
  },
  major: {
    _id: number;
    name_vn: string;
  }
}

interface ParamData {
  number?: string;
  type?: string;
  student_dob?: any,
  student_name_en?: string,
  page?: number;
  perPage?: number;
}

export default function HomeCertificate() {

  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<defaultRows[]>([]);
  const [dataFilters, setDataFilters] = useState<defaultRows[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [certificateData, setCertificateData] = useState<defaultRows>();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [paramData, setParamData] = useState<ParamData>({page, perPage});

  const table = useTable();

  const handleFilters = useCallback(
    (key: string, value: any) => {
      table.onResetPage();
      setPage(1)
      setParamData(prevParamData => ({
        ...prevParamData,
        page: 1
      }));

      if(key === "filterParam" && value){
        setParamData(prevParamData => ({
          ...prevParamData,
          number: value.number,
          type: value.type,
          student_dob: value.dob,
          student_name_en: value.name
        }));
      }

      // if(key === "filterParam" && value === ""){
      //   setParamData(prevParamData => {
      //     // eslint-disable-next-line @typescript-eslint/no-unused-vars
      //     const { number, ...rest } = prevParamData;
      //     return rest;
      //   })
      // }

    }, 
    [table]
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)

      try {
        const response = await axios.get(endpoints.student.certificate, {
          params: paramData
        });
        setData(response.data.data.data)
        setTotal(response.data.data.total)
        setDataFilters(response.data.data.data)
        setIsLoading(false)
      } catch (error) {
        setIsLoading(false)
        console.error('Error fetching data:', error);
      }
    };
    if(paramData.type !== undefined && paramData.number !== '' && paramData.number !== undefined && paramData.student_dob !== '' && paramData.student_name_en !== ''){
      fetchData();
    }else{
      setData([]);
      setTotal(0);
      setDataFilters([]);
    }
    

  }, [paramData]);

  const handleShowModal = (status:boolean) => {
    if(status === true){
      setShowModal(true)
    }else{
      setShowModal(false)
    }
  }

  const handleChangePage = (event:any, newPage:number) =>{    
    setPage(newPage + 1)
    setParamData(prevParamData => ({
      ...prevParamData,
      page: newPage + 1
    }));
  }

  const handleChangePerPage = (event:any) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPerPage(newRowsPerPage)
    setParamData(prevParamData => ({
      ...prevParamData,
      perPage: newRowsPerPage
    }));
  };

  const dfm = (inputDateString: any) => {
    const date = new Date(inputDateString);
    return format(date, 'dd/MM/yyyy');
  }
  const capitalize = (text:string) => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()

  return (
    <>
      <DetailsDialog certificateData={certificateData} status={handleShowModal} active={showModal}/>
      <Container>
        <Typography
          variant="h3"
          sx={{
            mt: 3,
            textAlign: 'center',
            textTransform: 'uppercase'
          }}
        >
          Tra cứu văn bằng chứng chỉ
        </Typography>

        <TableToolbar isLoading={isLoading} onFilters={handleFilters} />

        <Alert severity="info">
          Điều kiện tìm kiếm: Bao gồm <strong>tên văn bằng</strong>, <strong>số hiệu</strong>, <strong>họ tên</strong>, <strong>ngày sinh</strong>
        </Alert>
        
        <Card sx={{ marginTop: 3 }}>
          {isLoading && <LinearProgress />}
          {
            total <= 0 ?
              <>
                <EmptyContent />
                { paramData.number
                  ? <Typography sx={{ mb:3, textAlign: 'center', color: '#919EAB' }} variant="body1">Không tìm thấy kết quả cho số hiệu văn bằng <strong style={{color: '#00a76f'}}>{paramData.number}</strong></Typography>
                  : <Typography sx={{ mb:3, textAlign: 'center', color: '#919EAB' }} variant="body1">Thông tin không có sẵn</Typography>
                }
              </>
            :
            <>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tên văn bằng</TableCell>
                      <TableCell>Họ tên</TableCell>
                      <TableCell>Ngày sinh</TableCell>
                      <TableCell>Mã sinh viên</TableCell>
                      <TableCell>Số hiệu</TableCell>
                      <TableCell align="right">Chi tiết</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                  {dataFilters && dataFilters.length > 0 && dataFilters
                    .slice(
                      table.page * table.rowsPerPage,
                      table.page * table.rowsPerPage + table.rowsPerPage
                    )
                    .map((row) => (
                      <TableRow
                        key={row._id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell component="th" scope="row">                      
                          <Typography variant="body1">{row.type === 0 ? "Bằng Cử Nhân": "Bằng Bác Sĩ Y Khoa"}</Typography>
                        </TableCell>
                        <TableCell>                          
                          <Typography sx={{textTransform: "capitalize"}} variant="body1">{capitalize(row.student_name)}</Typography>
                        </TableCell>
                        <TableCell>{dfm(row.student_dob)}</TableCell>
                        <TableCell>{row.student_id_card}</TableCell>
                        <TableCell>{row.number}</TableCell>
                        <TableCell align="right">
                          <IconButton color="primary" onClick={() => {setShowModal(true); setCertificateData(row)}}>
                            <Iconify icon="ph:certificate" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            
            {total > 10 &&
              <Stack
                pr={3} 
                gap={2}
                direction="row"
                justifyContent="end"
                alignItems="center"
                >
                <TablePaginationCustom
                    count={total}
                    page={page - 1}
                    rowsPerPage={perPage}            
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangePerPage}
                />
                <Stack gap={2} direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">
                    Đến trang
                  </Typography>
                  <Input
                    type="number"
                    value={page}
                    onChange={(event) => {
                      const newPage = event.target.value ? parseInt(event.target.value, 10) : 1;
                      setPage(newPage)
                      setParamData(prevParamData => ({
                        ...prevParamData,
                        page: newPage
                      }));
                    }}
                    inputProps={{
                      min: 1,
                      max: Math.ceil(total / perPage),
                    }}
                  />
                </Stack>
              </Stack>
            }
          </>
          }
        </Card>
      </Container>
    </>
  );
}
